import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms'

import { isString as _isString } from 'lodash'
import { isString } from 'lodash/fp'

import { map, tap } from 'rxjs'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxSelectModule } from '@flowx/angular-ui-toolkit'
import {
  FormFieldsEnum,
  LabelAccessibilityProps,
  SelectTemplateConfig,
  isEnumerationCascadeChild,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxFieldOptionService } from '../../services/flx-field-option.service'
import { setControlValueDisplayOnly, setControlValueLate } from '../../utils/forms'
import { getContentIcon, getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

const toOptionKey = (value: unknown): string => {
  const primitive = value && typeof value === 'object' && 'value' in value ? value.value : value
  return String(primitive ?? '')
}

@Component({
  selector: 'flx-select-wrapper',
  imports: [
    CommonModule,
    FlxSelectModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './select-wrapper.component.html',
  styleUrl: './select-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxSelectWrapperComponent implements OnInit {
  #injector = inject(Injector)
  #optionsService = inject(FlxFieldOptionService)
  #destroyRef = inject(DestroyRef)

  // Only an enumeration cascade child still reconciles its value back into the store.
  readonly #writesBack = computed(() => isEnumerationCascadeChild(this.templateConfig()))
  #parentControl: AbstractControl | undefined
  // The parent value the options last emitted were fetched for.
  #optionsParentValue: unknown

  templateConfig = input.required<SelectTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => {
    const { leftIcon } = this.flowxProps()
    return {
      clear: getThemeIcon(ThemeComponent.Select, 'clearIcon'),
      rightIcon: getThemeIcon(ThemeComponent.Select, 'rightIcon'),
      helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
      leftIcon: getContentIcon(leftIcon),
    }
  })

  filteredOptions = computed(() => {
    const options = this.options() || []
    const query = this.query()
    if (!query) {
      return options
    }
    return options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
  })

  // dynamic props
  label: Signal<string>
  helper: Signal<string>
  placeholder: Signal<string>
  emptyMessage: Signal<string>
  ariaLabel: Signal<string>

  errorMessages: Record<string, Signal<string>>
  options: Signal<Array<{ label: string; value: Signal<string>; enabled?: boolean }>>
  query = signal<string>(null)

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  readonly CLEAR_LABEL = '@@sys_clear'

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const { label, helper, placeholder = '', emptyMessage } = this.flowxProps()
      this.label = getDynamicPropSignal(label, config)
      this.helper = getDynamicPropSignal(helper, config)
      this.placeholder = getDynamicPropSignal(placeholder, config)
      this.emptyMessage = getDynamicPropSignal(emptyMessage, config)
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = computed(() => this.accessibility()?.label)
      const formGroup = this.control().parent as FormGroup
      this.#parentControl = this.#writesBack()
        ? this.#optionsService.getEnumerationParentControl(config, formGroup)
        : undefined
      this.options = toSignal(
        this.#optionsService.getOptionsObs(config, formGroup, FormFieldsEnum.SELECT).pipe(
          // Setting enabled to true in case it's not provided
          map((options) =>
            options.map((option) => ({
              ...option,
              enabled: option.enabled !== undefined ? option.enabled : true,
            }))
          ),
          tap((options) => {
            this.#optionsParentValue = this.#parentControl?.value
            // make sure this is executed after the value is set
            setTimeout(() => this.#matchSelectedOption(options, this.control().value))
          })
        )
      )
    })
    // handle cases where value received for the control is a string (as is kept in the process data store)
    // but options from nomenclator need to be internally kept as objects
    // this is to make sure that the value is set correctly when the options are updated
    this.control()
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => setTimeout(() => this.#setStringValueFormOptions(value)))
  }

  filter(query: string): void {
    this.query.set(query)
  }

  #matchSelectedOption(
    options: Array<{ label: string; value: string }>,
    selectedOption: { label: string; value: string } | string
  ): void {
    if ((_isString(selectedOption) && options.length === 0) || !selectedOption) {
      return
    }

    let foundOption: { label: string; value: string } | undefined
    if (isString(selectedOption)) {
      foundOption = options.find((opt) => opt.value === selectedOption)
    } else {
      foundOption = options.find(
        (opt) => opt.value?.toString() === selectedOption.value?.toString()
      )
    }

    this.#applyOption(foundOption)
  }

  #setStringValueFormOptions(value: string): void {
    if (_isString(value) && this.options()?.length) {
      // A value written with its new parent waits for that parent's options (#matchSelectedOption)
      // instead of being judged, and cleared, against the previous parent's.
      if (this.#isAwaitingParentOptions()) {
        return
      }
      const foundOption = this.options().find((opt) => opt?.value?.toString() === value?.toString())
      this.#applyOption(foundOption)
    }
  }

  #isAwaitingParentOptions(): boolean {
    return (
      !!this.#parentControl &&
      toOptionKey(this.#parentControl.value) !== toOptionKey(this.#optionsParentValue)
    )
  }

  #applyOption(foundOption: { label: string; value: unknown } | undefined): void {
    if (this.#writesBack()) {
      setControlValueLate(this.control(), foundOption || null)
      this.control().updateValueAndValidity({ emitEvent: false })
      this.control().markAsPristine()
    } else if (foundOption && this.control().value !== foundOption) {
      // display matching: store primitive -> option object, no store write; emits so a cascade child sees it
      setControlValueDisplayOnly(this.control(), foundOption)
    }
  }
}
